import { concatPath, moment, TimeFormat } from '@feathr/hooks';
import type { TConstraints, TOptionalConstraint } from '@feathr/rachis';

import { contactInfoConstraints } from './mailing';
import { MailrailSingleSendEmailCampaign } from './mailrail_single_send';
import type { IMailrailBaseEmailCampaign, IMailrailPartnerMessage } from './types';

type PartialAttrs = Partial<IMailrailPartnerMessage>;

// Nullify the contact info constraints inherited from MailrailBaseEmailCampaign;
// partner messages don't collect a physical mailing address.
const nullifiedContactInfoConstraints: TConstraints<IMailrailBaseEmailCampaign> =
  Object.fromEntries(Object.keys(contactInfoConstraints).map((key) => [key, undefined]));

export class MailrailPartnerMessage extends MailrailSingleSendEmailCampaign<IMailrailPartnerMessage> {
  public override get constraints(): TConstraints<IMailrailPartnerMessage> {
    return {
      ...super.constraints,
      ...nullifiedContactInfoConstraints,
      'participation.mode': {
        presence: {
          allowEmpty: false,
          message: '^Please choose a partner selection mode.',
        },
      },
      'participation.partner_ids': (_: string[], attributes: PartialAttrs): TOptionalConstraint => {
        if (attributes.participation?.['mode'] === 'manual') {
          return {
            presence: {
              allowEmpty: false,
              message: '^You must specify at least one recipient if you choose "select partners".',
            },
          };
        }
        return undefined;
      },
      segments_excluded: undefined,
      segments_included: undefined,
    };
  }

  public override getItemUrl(pathSuffix?: string): string {
    return concatPath(`/projects/${this.get('event')}/partners/messages/${this.id}`, pathSuffix);
  }

  public getItemLabel(): string {
    if (this.get('name')) {
      return this.get('name');
    }
    const sendAt = this.get('date_start');
    const formattedDate = sendAt ? ` (${moment.utc(sendAt).format(TimeFormat.shortDateTime)})` : '';
    return `Unnamed message${formattedDate}`;
  }

  public override get name(): string {
    return this.get('name', '').trim() || `Unnamed Message`;
  }
}
